import DOMPurify, { Config } from "dompurify";

import { TrustedHTML, TrustedScript, TrustedScriptURL } from "trusted-types/lib";

/**
 * An interface representing a trusted type policy.
 */
export interface ITrustedTypePolicy {
  /**
   * Creates trusted HTML according to the policy.
   */
  createHTML?: (html: string) => TrustedHTML;

  /**
   * Creates trusted script code according to the policy.
   */
  createScript?: (script: string) => TrustedScript;

  /**
   * Creates a trusted script URL according to the policy
   */
  createScriptURL?: (src: string) => TrustedScriptURL;
}

/**
 * The trusted type policy manager service
 */
export interface ITrustedTypePolicyManager {
  /**
   * The default trusted type policy
   */
  defaultPolicy: ITrustedTypePolicy;

  /**
   * The trusted type policy for localized strings
   */
  stringsPolicy: ITrustedTypePolicy;

  /**
   * Initializes the trusted type manager and registers policies
   */
  init(): void;
}

/**
 * A service that manages trusted type policies for preventing cross-site scripting attacks
 */
export class TrustedTypePolicyManager implements ITrustedTypePolicyManager {
  /**
   * The default trusted type policy
   */
  public defaultPolicy: ITrustedTypePolicy;

  /**
   * The trusted type policy for localized strings
   */
  public stringsPolicy: ITrustedTypePolicy;

  /**
   * Initializes the trusted type manager and registers policies
   */
  public init(): void {
    if (!window.trustedTypes) {
      throw new Error("TrustedTypes support is not available even with the polyfill");
    }

    // We need to tell DomPurify how to manufacture trusted types. This looks potentially scary,
    // but it really is only used by DomPurify internally for manufcaturing types trusted by the browser.
    DOMPurify.setConfig({
      RETURN_TRUSTED_TYPE: true,
      TRUSTED_TYPES_POLICY: window.trustedTypes.createPolicy("rca-dompurify", {
        createHTML: (html: string) => html,
        createScript: () => {
          throw new Error("Script creation is not allowed by the DOMPurify trusted type policy");
        },
        createScriptURL: () => {
          throw new Error("Script URL creation is not allowed by the DOMPurify trusted type policy");
        },
      }),
    });

    // Create the default policy
    this.defaultPolicy = window.trustedTypes.createPolicy("default", new DefaultTrustedTypePolicyImpl());

    // Create policy for loading and rendering localized strings
    this.stringsPolicy = window.trustedTypes.createPolicy("rca-strings", new StringsTrustedTypePolicyImpl());
  }
}

/**
 * The default trusted type policy implementation
 */
class DefaultTrustedTypePolicyImpl {
  /**
   * Initializes the object
   */
  public constructor() {
    Object.seal(this);
  }

  /**
   * Processes HTML according to the policy
   *
   * @param html - The HTML to process
   * @returns - the Safe HTML
   */
  public createHTML = (html: string): string => {
    // Sanitize all HTML by default using DOMPurify
    return DOMPurify.sanitize(html);
  };

  /**
   * Allows blob: script URLs created by bundled Web Workers (e.g. the altcha PoW worker).
   * All other URLs are rejected.
   *
   * @param src - The script URL to evaluate
   * @returns The URL unchanged when it uses the blob: scheme
   */
  public createScriptURL = (src: string): string => {
    if (src.startsWith("blob:")) {
      return src;
    }
    throw new Error(`TrustedTypes default policy: disallowed script URL: ${src}`);
  };
}

/**
 * The trusted types policy implementation for localized strings
 */
class StringsTrustedTypePolicyImpl {
  /**
   * The regular expression for the known strings API paths
   */
  private stringsApiPathFormat = Object.seal(/^\/api\/.+?\/strings\/.+?\/(client|website)\/?$/);

  /**
   * The DOMPurify configuration for localized strings
   */
  private localizedStringConfig: Config = {
    ALLOWED_TAGS: ["strong", "b", "em", "i", "br", "a", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    ALLOW_DATA_ATTR: false,
  };

  /**
   * Initializes a new instance of the StringsTrustedTypePolicyImpl class.
   */
  public constructor() {
    DOMPurify.addHook("uponSanitizeElement", this.onSanitizeElement);
    Object.seal(this);
  }

  /**
   * Processes HTML according to the policy
   *
   * @param html - The HTML to process
   * @returns - the Safe HTML
   */
  public createHTML = (html: string): string => {
    return DOMPurify.sanitize(html, this.localizedStringConfig);
  };

  /**
   * Processes a script URL according to the policy
   *
   * @param src - the script URL to process
   * @returns - the safe script URL
   */
  public createScriptURL = (src: string) => {
    const uri = new URL(src, window.location.toString());
    if (uri.protocol !== window.location.protocol || uri.host !== window.location.host || !this.stringsApiPathFormat.test(uri.pathname)) {
      return "";
    }

    return src;
  };

  /**
   * Allows custom modifications to elements during sanitization
   *
   * @param currentNode - the current node
   */
  private onSanitizeElement = (currentNode: Element): void => {
    // Make sure any anchor elements in localized strings point to a new window with no referrer
    if (currentNode instanceof HTMLAnchorElement) {
      if (!currentNode.target) {
        currentNode.target = "_blank";
      }

      if (!currentNode.rel) {
        currentNode.rel = "noreferrer noopener";
      }
    }
  };
}
